/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { useFormatDateByUserProfileLocation } from '@leagueplatform/web-common';
import {
  MasonryChartAggregationBucketType,
  MasonryChartInterval,
} from '#types/masonry-chart.type.ts';
import { useGetDefaultTimezone } from '#hooks/use-get-default-timezone.hook.ts';
import * as RangeGraphTypes from './types/range-graph';
import { MasonryRangeGraph } from './masonry-range-graph';
import { formatMonthlyTick } from '../utils/use-monthly-ticks';
import {
  getIntervalXAxisTicks,
  getIntervalXAxisLabelTicks,
} from '../utils/get-interval-x-axis-ticks';

export type MasonryRangeGraphProps<Datum> = Omit<
  RangeGraphTypes.MasonryRangeGraph<Datum>,
  'xAxisTickFormat' | 'xAxisTicks' | 'xAxisLabelTicks' | 'barPadding'
> & {
  interval: MasonryChartInterval;
  aggregationBucketType: MasonryChartAggregationBucketType;
};

// Padding per (interval, bucket) combo. Tuned so bars stay visible
// regardless of how many buckets the BE produces. Higher = thinner bars.
const BAR_PADDING: Record<
  MasonryChartInterval,
  Partial<Record<MasonryChartAggregationBucketType, number>>
> = {
  weekly: { daily: 0.85 },
  monthly: { daily: 0.5, monthly: 0.95 },
  yearly: { daily: 0.2, monthly: 0.8, yearly: 0.95 },
  '5-yearly': { daily: 0.1, monthly: 0.4, yearly: 0.9 },
};

const DEFAULT_BAR_PADDING = 0.5;

export function MasonryRangeGraphIntervalSelector<Datum>({
  interval,
  aggregationBucketType,
  ...props
}: MasonryRangeGraphProps<Datum>) {
  const defaultTimezone = useGetDefaultTimezone();
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();

  const barPadding =
    BAR_PADDING[interval][aggregationBucketType] ?? DEFAULT_BAR_PADDING;
  const xAxisTicks = getIntervalXAxisTicks(
    props.data.map(props.accessors.xAccessor),
    interval,
    defaultTimezone,
  );
  const xAxisLabelTicks = getIntervalXAxisLabelTicks(
    props.data.map(props.accessors.xAccessor),
    interval,
    defaultTimezone,
  );

  if (interval === 'weekly') {
    return (
      <MasonryRangeGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            weekday: 'short',
          })
        }
        barPadding={barPadding}
        {...props}
      />
    );
  }
  if (interval === 'monthly') {
    return (
      <MasonryRangeGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        datumsPerColumn={1}
        xAxisTickFormat={formatMonthlyTick(
          formatDateWithUserProfileLocation,
          defaultTimezone,
        )}
        barPadding={barPadding}
        {...props}
      />
    );
  }
  if (interval === 'yearly') {
    return (
      <MasonryRangeGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            month: 'narrow',
          })
        }
        barPadding={barPadding}
        {...props}
      />
    );
  }
  if (interval === '5-yearly') {
    return (
      <MasonryRangeGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            year: 'numeric',
          })
        }
        barPadding={barPadding}
        {...props}
      />
    );
  }
  return null;
}
